import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

export function FeedbackBanner() {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Only process visits on the initial load of a new session or page change
        const checkVisits = () => {
            // If they are currently on the feedback page, don't show the banner
            if (location.pathname === '/feedback') {
                setIsVisible(false);
                return;
            }

            const isDismissed = localStorage.getItem('feedback_banner_dismissed');
            const hasGivenFeedback = localStorage.getItem('synthconst_feedback');

            if (isDismissed || hasGivenFeedback) {
                setIsVisible(false);
                return;
            }

            let visits = parseInt(localStorage.getItem('synthconst_visits') || '0', 10);

            // Increment visit count once per loaded session
            // For simplicity in a SPA we increment on component mount 
            visits += 1;
            localStorage.setItem('synthconst_visits', visits.toString());

            if (visits >= 3) {
                // Add a slight delay so it doesn't jarringly appear before content renders
                setTimeout(() => setIsVisible(true), 2500);
            }
        };

        // We use a simple session storage key to prevent incrementing the 'synthconst_visits' 
        // counter every single time they click a router link within the SPA.
        const sessionActive = sessionStorage.getItem('session_active');
        if (!sessionActive) {
            sessionStorage.setItem('session_active', '1');
            checkVisits();
        } else {
            // Just check visibility logic without incrementing the global counter
            if (location.pathname === '/feedback') {
                setIsVisible(false);
                return;
            }
            const visits = parseInt(localStorage.getItem('synthconst_visits') || '0', 10);
            const isDismissed = localStorage.getItem('feedback_banner_dismissed');
            const hasGivenFeedback = localStorage.getItem('synthconst_feedback');

            if (!isDismissed && !hasGivenFeedback && visits >= 3) {
                setTimeout(() => setIsVisible(true), 1500);
            } else {
                setIsVisible(false);
            }
        }
    }, [location.pathname]);

    const handleDismiss = () => {
        localStorage.setItem('feedback_banner_dismissed', 'true');
        setIsVisible(false);
    };

    const handleGiveFeedback = () => {
        setIsVisible(false);
        navigate('/feedback');
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 animate-in slide-in-from-bottom-5 fade-in duration-500">
            <Card className="w-[300px] sm:w-[350px] shadow-lg border-accent/20">
                <CardContent className="p-4">
                    <button
                        onClick={handleDismiss}
                        className="absolute right-2 top-2 p-1 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted"
                        aria-label="Dismiss feedback banner"
                    >
                        <X className="h-4 w-4" />
                    </button>

                    <div className="pr-4">
                        <h4 className="font-semibold text-sm mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{t('banner.title')}</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('banner.subtitle')}
                        </p>

                        <div className="flex space-x-2">
                            <Button size="sm" onClick={handleGiveFeedback} className="w-full">
                                {t('banner.giveFeedback')}
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleDismiss} className="w-full">
                                {t('banner.dismiss')}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
